import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CreditCard, Check, Sparkles, Tag, X } from "lucide-react";
import { useAuth } from "@/lib/auth";

export default function Billing() {
  const nav = useNavigate();
  const { user, logout } = useAuth();
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [couponInput, setCouponInput] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponBusy, setCouponBusy] = useState(false);

  useEffect(() => {
    api.get("/billing/status").then(({ data }) => setStatus(data)).catch(() => {});
  }, []);

  const applyCoupon = async () => {
    const code = couponInput.trim().toUpperCase();
    if (!code) return;
    setCouponBusy(true);
    try {
      const { data } = await api.post("/billing/coupon/validate", { code });
      setAppliedCoupon(data);
      toast.success(`${data.code} applied — ${data.percent_off}% off ${data.duration === "once" ? "first month" : "forever"}`);
    } catch (e) {
      setAppliedCoupon(null);
      toast.error(e.response?.data?.detail || "Invalid promo code");
    } finally { setCouponBusy(false); }
  };

  const clearCoupon = () => { setAppliedCoupon(null); setCouponInput(""); };

  const startCheckout = async () => {
    setBusy(true);
    try {
      const payload = { origin_url: window.location.origin };
      if (appliedCoupon) payload.coupon = appliedCoupon.code;
      const { data } = await api.post("/billing/checkout", payload);
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't start checkout");
      setBusy(false);
    }
  };

  const openPortal = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/billing/portal", { origin_url: window.location.origin });
      window.location.href = data.portal_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Portal unavailable");
      setBusy(false);
    }
  };

  const active = status?.is_active;
  const st = status?.subscription_status || "none";
  const priceCents = 2900;
  const discountCents = appliedCoupon ? Math.round(priceCents * (appliedCoupon.percent_off / 100)) : 0;
  const firstMonth = ((priceCents - discountCents) / 100).toFixed(2);

  return (
    <div className="min-h-screen bg-[#FAF7F2] p-6">
      <div className="max-w-md mx-auto">
        <div className="bg-white rounded-3xl border border-[#E5DCCE] p-8 shadow-sm space-y-5">
          <div className="text-center">
            <div className="w-14 h-14 rounded-2xl bg-[#F5E6CF] grid place-content-center mx-auto text-[#C85A17]">
              <Sparkles className="w-6 h-6"/>
            </div>
            <h1 className="font-display text-3xl font-bold mt-4">PawTrek Groomer Plan</h1>
            <p className="text-sm text-[#6B625B] mt-1">
              Hi {user?.name?.split(" ")[0]} — {active ? "you're all set." : "one card and you're rolling."}
            </p>
          </div>

          <div className="rounded-2xl bg-[#FAF7F2] border border-[#E5DCCE] p-5 text-center">
            {appliedCoupon ? (
              <>
                <div className="font-display text-4xl font-bold text-[#C85A17]">
                  ${firstMonth}<span className="text-lg text-[#6B625B]">/first mo</span>
                </div>
                <div className="text-xs text-[#6B625B] mt-1">
                  <span className="line-through mr-1">$29</span>
                  then $29/mo · {appliedCoupon.code} saved you {appliedCoupon.percent_off}%
                </div>
              </>
            ) : (
              <>
                <div className="font-display text-4xl font-bold">$29<span className="text-lg text-[#6B625B]">/mo</span></div>
                <div className="text-xs text-[#6B625B] mt-1">7-day free trial · cancel anytime</div>
              </>
            )}
          </div>

          <ul className="space-y-2 text-sm">
            {["Unlimited clients & appointments","Stripe payments + tipping + refunds",
              "AI reply drafts (Claude Sonnet 5)","Before/after pet galleries + share links",
              "Weekly revenue + CSV export"].map((x) => (
              <li key={x} className="flex items-start gap-2">
                <Check className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0"/> {x}
              </li>
            ))}
          </ul>

          {!active && (
            <div className="rounded-2xl bg-[#F3EDE4] border border-[#E5DCCE] p-3 space-y-2" data-testid="coupon-panel">
              <div className="flex items-center gap-2 text-xs font-semibold text-[#6B4A18] uppercase tracking-wider">
                <Tag className="w-3.5 h-3.5"/> Promo code
              </div>
              {appliedCoupon ? (
                <div className="flex items-center justify-between bg-white rounded-xl border border-emerald-200 p-2 pl-3">
                  <div className="text-sm">
                    <span className="font-bold text-emerald-700" data-testid="applied-coupon">{appliedCoupon.code}</span>
                    <span className="text-[#6B625B] ml-2 text-xs">{appliedCoupon.percent_off}% off</span>
                  </div>
                  <button onClick={clearCoupon} className="p-1 hover:bg-[#F3EDE4] rounded-full"
                    data-testid="btn-clear-coupon" aria-label="Remove coupon">
                    <X className="w-4 h-4 text-[#6B625B]"/>
                  </button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <Input value={couponInput} onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
                    placeholder="e.g. LAUNCH50" className="rounded-full h-10 bg-white"
                    data-testid="input-coupon"
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); applyCoupon(); } }}/>
                  <Button onClick={applyCoupon} disabled={couponBusy || !couponInput.trim()}
                    variant="outline" className="rounded-full h-10 px-4 border-[#C85A17] text-[#C85A17] hover:bg-[#C85A17] hover:text-white"
                    data-testid="btn-apply-coupon">
                    {couponBusy ? "…" : "Apply"}
                  </Button>
                </div>
              )}
            </div>
          )}

          <div className="text-xs text-center py-2 rounded-xl bg-[#F3EDE4]" data-testid="sub-status">
            Status: <span className="font-semibold uppercase tracking-wider">{st}</span>
          </div>

          {active ? (
            <>
              <Button onClick={() => nav("/groomer")}
                className="w-full rounded-full bg-[#C85A17] hover:bg-[#A9480F] h-12" data-testid="btn-go-dashboard">
                Go to dashboard
              </Button>
              <Button onClick={openPortal} disabled={busy} variant="outline"
                className="w-full rounded-full" data-testid="btn-portal">
                Manage subscription
              </Button>
            </>
          ) : (
            <Button onClick={startCheckout} disabled={busy}
              className="w-full rounded-full bg-[#C85A17] hover:bg-[#A9480F] btn-tactile h-12"
              data-testid="btn-start-trial">
              <CreditCard className="w-4 h-4 mr-2"/>
              {busy ? "Redirecting…" : "Start 7-day trial"}
            </Button>
          )}

          <button onClick={async () => { await logout(); nav("/"); }}
            className="w-full text-xs text-[#6B625B] hover:text-[#1F1A17] pt-2" data-testid="btn-billing-logout">
            Sign out
          </button>
        </div>
      </div>
    </div>
  );
}
